import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { FaChartLine, FaFilter, FaMapMarkerAlt, FaSearch, FaTimes } from 'react-icons/fa';
import Spinner from './Components/Spinner';
import { SearchResultCard } from './SearchResults';
import { useAuth } from './AuthContext';
import { getVehiclePath } from './Utils/vehicleUrls';
import { getStoredZip, isValidZip, persistRecentZip, requestLocationZip } from './Utils/locationZip';
import { measureOpenAIContentsViewed } from './Utils/openaiAdsPixel';

const CANONICAL_URL = 'https://meshum.com/insights/best-deals';
const PAGE_SIZE = 20;
const DEFAULT_SORT = 'year_desc';
const SORT_LABELS = {
  best_value: 'Strongest deals',
  difference_percent: 'Largest percentage difference',
  difference_amount: 'Largest dollar difference',
  price_asc: 'Lowest price',
  year_desc: 'Newest year',
  mileage_asc: 'Lowest mileage',
  recent: 'Recently listed',
};
const BEST_DEALS_CONFIG = {
  heading: 'Best Used-Car Deals',
  copy: 'Best Deals surfaces active used vehicles priced below their Meshum Estimate, based on comparable vehicles that recently sold.',
  countLabel: 'Qualifying vehicles',
  amountLabel: 'Median below estimate',
  percentLabel: 'Median percent below',
  cardLabel: 'Below estimate',
  emptyTitle: 'No Best Deals match these filters',
  emptyCopy: 'Try increasing the radius, raising your budget, removing make or model filters, or viewing nationwide results.',
};
const VALID_RADIUS_VALUES = new Set(['25', '50', '100', '250', '500', '9999']);

function currency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not available';
  return `$${Math.round(number).toLocaleString()}`;
}

function percent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not available';
  return `${(number * 100).toFixed(1)}%`;
}

function number(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toLocaleString();
}

function formatUpdated(value) {
  if (!value) return 'Updated daily';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Updated daily';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function queryValue(params, key, fallback = '') {
  return params.get(key) || fallback;
}

export function findCanonicalOption(options, value) {
  const requested = String(value || '').trim().toLocaleLowerCase('en-US');
  if (!requested) return '';
  return options.find((option) => String(option).trim().toLocaleLowerCase('en-US') === requested) || '';
}

function queryZipValue(params) {
  const value = params.get('zip');
  return isValidZip(value) ? value.trim() : '';
}

function queryRadiusValue(params, validZip) {
  const radius = params.get('radius');
  if (!validZip) return '9999';
  if (VALID_RADIUS_VALUES.has(radius)) return radius;
  return '250';
}

function normalizeBestDealsParams(sourceParams, validZip, effectiveRadius) {
  const next = new URLSearchParams(sourceParams);
  next.delete('position');
  if (validZip) {
    next.set('zip', validZip);
    next.set('radius', effectiveRadius);
  } else {
    next.delete('zip');
    next.delete('radius');
  }
  return next;
}

function InsightsBestDeals({ market = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { user } = useAuth();
  const isStaticPrerender = window.location.hostname === '127.0.0.1';
  const [clientReady, setClientReady] = useState(false);
  const params = useMemo(
    () => new URLSearchParams(clientReady ? location.search : ''),
    [clientReady, location.search]
  );
  const currentPage = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
  const basePath = market?.path || '/insights/best-deals';
  const canonicalUrl = market?.canonicalUrl || CANONICAL_URL;
  const currentZip = market?.centerZip || queryZipValue(params);
  const currentRadius = market?.radius || queryRadiusValue(params, currentZip);

  const [filters, setFilters] = useState({
    zip: currentZip,
    radius: currentRadius,
    minPrice: queryValue(params, 'minPrice'),
    maxPrice: queryValue(params, 'maxPrice'),
    make: queryValue(params, 'make'),
    model: queryValue(params, 'model'),
    minYear: queryValue(params, 'minYear'),
    maxMileage: queryValue(params, 'maxMileage'),
    sort: queryValue(params, 'sort', DEFAULT_SORT),
  });
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [cars, setCars] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  const [invalidZip, setInvalidZip] = useState(false);
  const [savedCarIds, setSavedCarIds] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [locationReady, setLocationReady] = useState(false);
  const locationInitializationStarted = useRef(false);
  const entryTracked = useRef(false);

  useEffect(() => {
    if (!isStaticPrerender) setClientReady(true);
  }, [isStaticPrerender]);

  useEffect(() => {
    if (market && clientReady) setLocationReady(true);
  }, [clientReady, market]);

  useEffect(() => {
    if (!clientReady || market) return undefined;
    if (params.has('position')) {
      const next = new URLSearchParams(location.search);
      next.delete('position');
      navigate(`/insights/best-deals${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
      return;
    }
    if (currentZip) {
      persistRecentZip(currentZip);
      setLocationReady(true);
      return;
    }
    if (params.get('radius') === '9999') {
      setLocationReady(true);
      return;
    }
    if (locationInitializationStarted.current) return;
    locationInitializationStarted.current = true;

    const savedZip = getStoredZip();
    if (savedZip) {
      const next = new URLSearchParams(location.search);
      next.set('zip', savedZip);
      next.set('radius', '250');
      navigate(`/insights/best-deals?${next.toString()}`, { replace: true });
      return;
    }

    let cancelled = false;
    requestLocationZip().then((locatedZip) => {
      if (cancelled) return;
      if (locatedZip) {
        const next = new URLSearchParams(location.search);
        next.set('zip', locatedZip);
        next.set('radius', '250');
        navigate(`/insights/best-deals?${next.toString()}`, { replace: true });
      } else {
        setLocationReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [clientReady, currentZip, location.search, market, navigate, params]);

  useEffect(() => {
    setFilters({
      zip: currentZip,
      radius: currentRadius,
      minPrice: queryValue(params, 'minPrice'),
      maxPrice: queryValue(params, 'maxPrice'),
      make: queryValue(params, 'make'),
      model: queryValue(params, 'model'),
      minYear: queryValue(params, 'minYear'),
      maxMileage: queryValue(params, 'maxMileage'),
      sort: queryValue(params, 'sort', DEFAULT_SORT),
    });
  }, [currentRadius, currentZip, params]);

  useEffect(() => {
    if (!locationReady || entryTracked.current) return;
    entryTracked.current = true;
    window.gtag?.('event', 'view_best_deals', {
      page: currentPage,
      has_location: Boolean(currentZip),
      selected_sort: params.get('sort') || DEFAULT_SORT,
    });
  }, [currentPage, currentZip, locationReady, params]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/makes`)
      .then((res) => res.json())
      .then((data) => setMakeOptions(data))
      .catch(() => setMakeOptions([]));
  }, []);

  useEffect(() => {
    if (!filters.make || makeOptions.length === 0) return;
    const canonicalMake = findCanonicalOption(makeOptions, filters.make);
    if (canonicalMake === filters.make) return;
    setFilters((previous) => ({
      ...previous,
      make: canonicalMake,
      model: canonicalMake ? previous.model : '',
    }));
  }, [filters.make, makeOptions]);

  useEffect(() => {
    if (!filters.make) {
      setModelOptions([]);
      return;
    }
    if (makeOptions.length === 0 || findCanonicalOption(makeOptions, filters.make) !== filters.make) {
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE}/cars/models?make=${encodeURIComponent(filters.make)}`)
      .then((res) => res.json())
      .then((data) => setModelOptions(data))
      .catch(() => setModelOptions([]));
  }, [filters.make, makeOptions]);

  useEffect(() => {
    if (!filters.model || modelOptions.length === 0) return;
    const canonicalModel = findCanonicalOption(modelOptions, filters.model);
    if (canonicalModel === filters.model) return;
    setFilters((previous) => ({ ...previous, model: canonicalModel }));
  }, [filters.model, modelOptions]);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${process.env.REACT_APP_API_BASE}/saved/${user.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => res.json())
      .then((data) => setSavedCarIds(data))
      .catch(() => setSavedCarIds([]));
  }, [user]);

  useEffect(() => {
    if (!locationReady) return undefined;
    const controller = new AbortController();
    const requestParams = normalizeBestDealsParams(new URLSearchParams(location.search), currentZip, currentRadius);
    requestParams.set('pageSize', PAGE_SIZE.toString());
    requestParams.delete('position');
    if (!requestParams.get('sort')) requestParams.set('sort', DEFAULT_SORT);

    setLoading(true);
    setError('');

    fetch(`${process.env.REACT_APP_API_BASE}/insights/best-deals?${requestParams.toString()}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setCars(data?.results || []);
        setSummary(data?.summary || null);
        setTotalPages(data?.totalPages || 0);
        setTotalResults(data?.totalResults || 0);
        setLastUpdated(data?.lastUpdatedUtc || null);
        setInvalidZip(Boolean(data?.invalidZip));
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        if (!isStaticPrerender) {
          setError('Best Deals are temporarily unavailable. Please try again shortly.');
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [currentRadius, currentZip, isStaticPrerender, location.search, locationReady, retryToken]);

  useEffect(() => {
    if (navigationType === 'POP') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.search, navigationType]);

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'make') next.model = '';
      if (key === 'zip') {
        const nextZip = String(value || '').trim();
        if (!nextZip) next.radius = '9999';
        else if (isValidZip(nextZip) && prev.radius === '9999') next.radius = '250';
      }
      return next;
    });
  };

  const applyFilters = () => {
    const next = new URLSearchParams();
    const filterZip = isValidZip(filters.zip) ? filters.zip.trim() : '';
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'zip' || key === 'radius') return;
      if (value) next.set(key, value);
    });
    if (filterZip) {
      next.set('zip', filterZip);
      next.set('radius', VALID_RADIUS_VALUES.has(filters.radius) ? filters.radius : '250');
    }
    next.set('page', '1');
    if (filterZip) persistRecentZip(filterZip);

    window.gtag?.('event', 'best_deals_filter', {
      action: 'apply',
      radius: filterZip ? (VALID_RADIUS_VALUES.has(filters.radius) ? filters.radius : '250') : '9999',
      make: filters.make || '',
      model: filters.model || '',
      min_price: filters.minPrice || '',
      max_price: filters.maxPrice || '',
      min_year: filters.minYear || '',
      max_mileage: filters.maxMileage || '',
      selected_sort: filters.sort || DEFAULT_SORT,
    });

    setFiltersOpen(false);
      navigate(`${basePath}?${next.toString()}`);
  };

  const clearFilters = () => {
    window.gtag?.('event', 'best_deals_filter', {
      action: 'clear',
      radius: currentRadius,
      make: params.get('make') || '',
      model: params.get('model') || '',
      min_price: params.get('minPrice') || '',
      max_price: params.get('maxPrice') || '',
      min_year: params.get('minYear') || '',
      max_mileage: params.get('maxMileage') || '',
      selected_sort: params.get('sort') || DEFAULT_SORT,
    });
    setFilters({
      zip: '',
      radius: '9999',
      minPrice: '',
      maxPrice: '',
      make: '',
      model: '',
      minYear: '',
      maxMileage: '',
      sort: DEFAULT_SORT,
    });
    setFiltersOpen(false);
    navigate(basePath);
  };

  const changeSort = (value) => {
    const next = normalizeBestDealsParams(new URLSearchParams(location.search), currentZip, currentRadius);
    if (value && value !== DEFAULT_SORT) next.set('sort', value);
    else next.delete('sort');
    next.set('page', '1');
    window.gtag?.('event', 'best_deals_sort', {
      selected_sort: value || DEFAULT_SORT,
    });
    navigate(`${basePath}?${next.toString()}`);
  };

  const goToPage = (page) => {
    const next = normalizeBestDealsParams(new URLSearchParams(location.search), currentZip, currentRadius);
    next.set('page', page);
    window.gtag?.('event', 'best_deals_pagination', {
      page,
    });
    navigate(`${basePath}?${next.toString()}`);
  };

  const handleCarClick = (car, index) => {
    window.gtag?.('event', 'best_deals_vehicle_click', {
      car_id: car.id,
      make: car.make || '',
      model: car.model || '',
      asking_price: car.price,
      mestimate: car.mestimate,
      savings_amount: car.savingsAmount,
      savings_percent: car.savingsPercent,
      difference_amount: car.differenceAmount,
      difference_percent: car.differencePercent,
      result_position: (currentPage - 1) * PAGE_SIZE + index + 1,
      selected_sort: params.get('sort') || DEFAULT_SORT,
      radius: currentRadius,
    });

    measureOpenAIContentsViewed();

    navigate(getVehiclePath(car), {
      state: { from: { pathname: location.pathname, search: location.search } },
    });
  };

  const handleDealerClick = (car, index) => {
    window.gtag?.('event', 'best_deals_dealer_click', {
      dealer_id: car.dealerId,
      car_id: car.id,
      make: car.make || '',
      model: car.model || '',
      asking_price: car.price,
      mestimate: car.mestimate,
      savings_amount: car.savingsAmount,
      savings_percent: car.savingsPercent,
      difference_amount: car.differenceAmount,
      difference_percent: car.differencePercent,
      result_position: (currentPage - 1) * PAGE_SIZE + index + 1,
      selected_sort: params.get('sort') || DEFAULT_SORT,
      radius: currentRadius,
    });
  };

  const handleToggleSavedCar = async (carId, currentlySaved) => {
    if (!user?.id) return { ok: false, reason: 'auth' };
    try {
      const method = currentlySaved ? 'DELETE' : 'POST';
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/saved`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, carId }),
      });
      if (!res.ok) return { ok: false };
      setSavedCarIds((prev) => currentlySaved ? prev.filter((id) => id !== carId) : Array.from(new Set([...prev, carId])));
      return { ok: true };
    } catch {
      return { ok: false };
    }
  };

  const hasFilters = [...params.keys()].some((key) => key !== 'page');
  const robots = currentPage > 1 || hasFilters ? 'noindex,follow' : 'index,follow';
  const contextLabel = currentZip && currentRadius !== '9999'
    ? `near ${currentZip} within ${currentRadius} miles`
    : 'nationwide';
  const compactContextLabel = currentZip && currentRadius !== '9999'
    ? `Near ${currentZip} · ${currentRadius} miles`
    : 'Nationwide';
  const medianAmount = currency(Math.abs(Number(summary?.medianDifferenceAmount ?? summary?.medianSavingsAmount)));
  const medianPercent = percent(Math.abs(Number(summary?.medianDifferencePercent ?? summary?.medianSavingsPercent)));
  const resultsLabel = loading
    ? 'Loading Best Deals...'
    : cars.length > 0
      ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}-${(currentPage - 1) * PAGE_SIZE + cars.length} of ${number(totalResults)} vehicles`
      : 'No qualifying vehicles found';

  const pageName = market?.heading || 'Best Used-Car Deals';
  const pageDescription = market?.description || 'Find active used cars priced below their Meshum Estimate, based on comparable vehicles that recently sold. Filter by budget, location, make, and model.';
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Best Used-Car Deals Below Meshum Estimate',
    itemListElement: cars.slice(0, 10).map((car, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://meshum.com${getVehiclePath(car)}`,
      name: `${car.year} ${car.make} ${car.model}`.trim(),
    })),
  };

  return (
    <>
      <Helmet>
        <title>{market?.title || 'Best Used-Car Deals Below Market Value | Meshum'}</title>
        <meta
          name="description"
          content={pageDescription}
        />
        <meta name="robots" content={robots} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={market?.title || 'Best Used-Car Deals Below Market Value | Meshum'} />
        <meta
          property="og:description"
          content={market ? pageDescription : 'Explore active vehicles priced below their Meshum Estimate using sold comparable vehicle data.'}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://meshum.com/share-thumbnail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={market?.title || 'Best Used-Car Deals Below Market Value | Meshum'} />
        <meta
          name="twitter:description"
          content={market ? pageDescription : 'Find active used cars priced below their Meshum Estimate and filter by budget, location, make, and model.'}
        />
        <meta name="twitter:image" content="https://meshum.com/share-thumbnail.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://meshum.com/' },
              { '@type': 'ListItem', position: 2, name: 'Best Deals', item: CANONICAL_URL },
              ...(market ? [{ '@type': 'ListItem', position: 3, name: `${market.city}, ${market.stateCode}`, item: canonicalUrl }] : []),
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageName,
            description: market ? pageDescription : 'Active used vehicles priced below their Meshum Estimate.',
            url: canonicalUrl,
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      <main className="bg-slate-50">
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 md:py-8 lg:py-10">
            <nav className="mb-5 hidden text-sm text-slate-500 md:block" aria-label="Breadcrumb">
              <Link to="/" className="text-slate-600 hover:text-blue-700">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-slate-600">Insights</span>
              <span className="mx-2">/</span>
              {market ? <Link to={CANONICAL_URL.replace('https://meshum.com', '')} className="text-slate-600 hover:text-blue-700">Best Deals</Link> : <span className="text-slate-900">Best Deals</span>}
              {market && <><span className="mx-2">/</span><span className="text-slate-900">{`${market.city}, ${market.stateCode}`}</span></>}
            </nav>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
              <div>
                <div className="hidden items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 md:inline-flex">
                  <FaChartLine />
                  Insights
                </div>
                <h1 className="text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl md:mt-4 md:text-4xl">
                  {pageName}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 md:mt-3 md:text-base md:leading-7">
                  {market
                    ? `Find used cars priced below their Meshum Estimate near ${market.city}. Compare asking prices with market estimates supported by recently sold comparable vehicles.`
                    : BEST_DEALS_CONFIG.copy}
                </p>
                {market && (
                  <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-700">
                    {`This market includes qualifying vehicles within ${market.radius} miles of ZIP ${market.centerZip} in central ${market.city}.`}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-600 md:mt-4">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 md:hidden">
                    {compactContextLabel}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
                    {`Last updated: ${formatUpdated(lastUpdated)}`}
                  </span>
                  <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 md:inline-flex">
                    {`Showing ${contextLabel}`}
                  </span>
                  <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 md:inline-flex">
                    {market && !summary ? `Loading current ${market.city} inventory...` : `${number(totalResults)} qualifying vehicles`}
                  </span>
                </div>
              </div>

              <div className="hidden rounded-lg border border-slate-200 bg-slate-50 p-4 md:block">
                <h2 className="text-base font-semibold text-slate-950">How vehicles qualify</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Meshum compares each vehicle's current asking price with its estimated market value based on recently sold comparable vehicles. Every result is priced below its Meshum Estimate and requires a current active listing, a fresh supported estimate, and enough comparable evidence.
                </p>
              </div>
            </div>

            <details className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 md:hidden">
              <summary className="cursor-pointer text-sm font-semibold text-slate-900">How Best Deals works</summary>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Meshum compares each vehicle's current asking price with its estimated market value based on recently sold comparable vehicles. Every result is priced below its Meshum Estimate and requires a current active listing, a fresh supported estimate, and enough comparable evidence.
              </p>
            </details>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:hidden">
            <div className="text-2xl font-semibold text-slate-950">
              {summary ? `${number(summary.qualifyingVehicles ?? totalResults)} deals` : market ? `Loading current ${market.city} deals...` : '0 deals'}
            </div>
            <div className="mt-1 text-sm leading-6 text-slate-600">
              {summary ? `Median ${medianAmount} below estimate · ${medianPercent} below estimate` : market ? 'Current local statistics will appear shortly.' : 'Median Not available below estimate'}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {summary ? `${number(summary.representedDealers)} represented dealers` : market ? `Checking represented ${market.city}-area dealers.` : '0 represented dealers'}
            </div>
          </div>

          <div className="hidden gap-4 md:grid md:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{BEST_DEALS_CONFIG.countLabel}</div>
              <div className="mt-2 text-2xl font-semibold text-slate-950">{summary ? number(summary.qualifyingVehicles ?? totalResults) : market ? 'Loading...' : '0'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{BEST_DEALS_CONFIG.amountLabel}</div>
              <div className="mt-2 text-2xl font-semibold text-slate-950">{summary ? medianAmount : market ? 'Loading...' : 'Not available'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{BEST_DEALS_CONFIG.percentLabel}</div>
              <div className="mt-2 text-2xl font-semibold text-slate-950">{summary ? medianPercent : market ? 'Loading...' : 'Not available'}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Represented dealers</div>
              <div className="mt-2 text-2xl font-semibold text-slate-950">{summary ? number(summary.representedDealers) : market ? 'Loading...' : '0'}</div>
            </div>
          </div>

          <div className="mt-4 flex gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm"
            >
              <FaFilter className="mr-2 text-slate-500" />
              Filters
            </button>
            <label className="min-w-0 flex-1">
              <span className="sr-only">Sort</span>
              <select
                value={params.get('sort') || DEFAULT_SORT}
                onChange={(e) => changeSort(e.target.value)}
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-950 shadow-sm"
              >
                {Object.entries(SORT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>

          <div className="mt-5 hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:block">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-950">
              <FaFilter className="text-slate-500" />
              Refine Best Deals
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {market ? (
                <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 lg:col-span-2">
                  <span className="font-semibold">{`${market.city} market:`}</span>{` within ${market.radius} miles of ZIP ${market.centerZip}`}
                </div>
              ) : <label>
                <span className="ui-field-label">ZIP code</span>
                <input value={filters.zip} onChange={(e) => updateFilter('zip', e.target.value)} placeholder="Nationwide" inputMode="numeric" />
              </label>}
              {!market && <label>
                <span className="ui-field-label">Radius</span>
                <select value={filters.radius} onChange={(e) => updateFilter('radius', e.target.value)}>
                  <option value="9999">Nationwide</option>
                  <option value="25">25 miles</option>
                  <option value="50">50 miles</option>
                  <option value="100">100 miles</option>
                  <option value="250">250 miles</option>
                  <option value="500">500 miles</option>
                </select>
              </label>}
              <label>
                <span className="ui-field-label">Max budget</span>
                <input type="number" value={filters.maxPrice} onChange={(e) => updateFilter('maxPrice', e.target.value)} placeholder="Any" />
              </label>
              <label>
                <span className="ui-field-label">Min budget</span>
                <input type="number" value={filters.minPrice} onChange={(e) => updateFilter('minPrice', e.target.value)} placeholder="Any" />
              </label>
              <label>
                <span className="ui-field-label">Make</span>
                <select value={filters.make} onChange={(e) => updateFilter('make', e.target.value)}>
                  <option value="">All makes</option>
                  {makeOptions.map((make) => <option key={make} value={make}>{make}</option>)}
                </select>
              </label>
              <label>
                <span className="ui-field-label">Model</span>
                <select value={filters.model} onChange={(e) => updateFilter('model', e.target.value)} disabled={!filters.make}>
                  <option value="">All models</option>
                  {modelOptions.map((model) => <option key={model} value={model}>{model}</option>)}
                </select>
              </label>
              <label>
                <span className="ui-field-label">Minimum year</span>
                <input type="number" value={filters.minYear} onChange={(e) => updateFilter('minYear', e.target.value)} placeholder="Any" />
              </label>
              <label>
                <span className="ui-field-label">Maximum mileage</span>
                <select value={filters.maxMileage} onChange={(e) => updateFilter('maxMileage', e.target.value)}>
                  <option value="">Any mileage</option>
                  <option value="30000">Under 30,000</option>
                  <option value="60000">Under 60,000</option>
                  <option value="90000">Under 90,000</option>
                  <option value="120000">Under 120,000</option>
                </select>
              </label>
              <label>
                <span className="ui-field-label">Sort</span>
                <select value={params.get('sort') || DEFAULT_SORT} onChange={(e) => changeSort(e.target.value)}>
                  {Object.entries(SORT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={applyFilters} className="btn-primary">
                <FaSearch className="mr-2" />
                Apply filters
              </button>
              <button type="button" onClick={clearFilters} className="btn-secondary">
                <FaTimes className="mr-2" />
                Clear
              </button>
              <Link
                to="/search"
                onClick={() => window.gtag?.('event', 'best_deals_search_click', {
                  source: 'best_deals',
                })}
                className="btn-secondary no-underline"
              >
                Search all cars
              </Link>
            </div>
          </div>

          {filtersOpen && (
            <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Best Deals filters">
              <button
                type="button"
                className="absolute inset-0 bg-slate-950/50"
                aria-label="Close filters"
                onClick={() => setFiltersOpen(false)}
              />
              <div className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-2xl bg-white p-4 shadow-2xl">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="text-lg font-semibold text-slate-950">Filters</div>
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(false)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600"
                    aria-label="Close filters"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid gap-3">
                    {market ? (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                        <span className="font-semibold">{`${market.city} market:`}</span>{` within ${market.radius} miles of ZIP ${market.centerZip}`}
                      </div>
                    ) : <label>
                      <span className="ui-field-label">ZIP code</span>
                      <input value={filters.zip} onChange={(e) => updateFilter('zip', e.target.value)} placeholder="Nationwide" inputMode="numeric" />
                    </label>}
                    {!market && <label>
                      <span className="ui-field-label">Radius</span>
                      <select value={filters.radius} onChange={(e) => updateFilter('radius', e.target.value)}>
                        <option value="9999">Nationwide</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                        <option value="250">250 miles</option>
                        <option value="500">500 miles</option>
                      </select>
                    </label>}
                    <label>
                      <span className="ui-field-label">Max budget</span>
                      <input type="number" value={filters.maxPrice} onChange={(e) => updateFilter('maxPrice', e.target.value)} placeholder="Any" />
                    </label>
                    <label>
                      <span className="ui-field-label">Min budget</span>
                      <input type="number" value={filters.minPrice} onChange={(e) => updateFilter('minPrice', e.target.value)} placeholder="Any" />
                    </label>
                    <label>
                      <span className="ui-field-label">Make</span>
                      <select value={filters.make} onChange={(e) => updateFilter('make', e.target.value)}>
                        <option value="">All makes</option>
                        {makeOptions.map((make) => <option key={make} value={make}>{make}</option>)}
                      </select>
                    </label>
                    <label>
                      <span className="ui-field-label">Model</span>
                      <select value={filters.model} onChange={(e) => updateFilter('model', e.target.value)} disabled={!filters.make}>
                        <option value="">All models</option>
                        {modelOptions.map((model) => <option key={model} value={model}>{model}</option>)}
                      </select>
                    </label>
                    <label>
                      <span className="ui-field-label">Minimum year</span>
                      <input type="number" value={filters.minYear} onChange={(e) => updateFilter('minYear', e.target.value)} placeholder="Any" />
                    </label>
                    <label>
                      <span className="ui-field-label">Maximum mileage</span>
                      <select value={filters.maxMileage} onChange={(e) => updateFilter('maxMileage', e.target.value)}>
                        <option value="">Any mileage</option>
                        <option value="30000">Under 30,000</option>
                        <option value="60000">Under 60,000</option>
                        <option value="90000">Under 90,000</option>
                        <option value="120000">Under 120,000</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="sticky bottom-0 mt-5 grid gap-2 border-t border-slate-200 bg-white pt-4">
                  <button type="button" onClick={applyFilters} className="btn-primary w-full justify-center">
                    <FaSearch className="mr-2" />
                    Apply filters
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={clearFilters} className="btn-secondary justify-center">
                      <FaTimes className="mr-2" />
                      Clear
                    </button>
                    <Link
                      to="/search"
                      onClick={() => {
                        setFiltersOpen(false);
                        window.gtag?.('event', 'best_deals_search_click', {
                          source: 'best_deals',
                        });
                      }}
                      className="btn-secondary justify-center no-underline"
                    >
                      Search all cars
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600">
              {resultsLabel}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <FaMapMarkerAlt className="text-slate-400" />
              {contextLabel}
            </div>
          </div>

          {error && (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" className="ml-3 underline" onClick={() => setRetryToken((value) => value + 1)}>
                Retry
              </button>
            </div>
          )}

          {loading ? (
            <Spinner />
          ) : !error && cars.length === 0 ? (
            <div className="mt-5 rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">
                {invalidZip ? 'That ZIP code is not supported yet' : BEST_DEALS_CONFIG.emptyTitle}
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600">
                {invalidZip ? 'Try another ZIP code or view nationwide results.' : BEST_DEALS_CONFIG.emptyCopy}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <button type="button" onClick={clearFilters} className="btn-primary">View nationwide results</button>
              </div>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {cars.map((car, index) => (
                <SearchResultCard
                  key={car.id}
                  car={{ ...car, priceLabel: BEST_DEALS_CONFIG.cardLabel }}
                  saved={savedCarIds.includes(car.id)}
                  userId={user?.id}
                  onOpen={() => handleCarClick(car, index)}
                  onToggleSaved={handleToggleSavedCar}
                  onDealerClick={() => handleDealerClick(car, index)}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && !loading && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-center">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="btn-primary bg-teal-600 hover:bg-teal-700" type="button">
                Prev
              </button>
              <span className="text-sm font-semibold text-slate-700">Page {currentPage} of {totalPages}</span>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages} className="btn-primary bg-teal-600 hover:bg-teal-700" type="button">
                Next
              </button>
            </div>
          )}

          <section className="mt-10 grid gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">What Meshum Estimate means</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Meshum Estimate is an estimated market value based on comparable vehicles that recently sold. It is a pricing signal, not a guarantee of final transaction price.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Why prices may change</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Dealer prices, availability, fees, incentives, vehicle condition, and final transaction prices may vary. Verify vehicle details with the dealer before making a decision.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">How results are ranked</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                The default order prioritizes the newest model years. Other sort choices let you prioritize deal strength, price, mileage, or savings directly.
              </p>
            </div>
          </section>
          {market?.slug === 'miami-fl' && (
            <section className="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-5">
              <h2 className="text-lg font-semibold text-slate-950">Miami used-car market research</h2>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                See Meshum's frozen August 2026 analysis of active Miami-area inventory, estimate coverage, and below-estimate pricing.
              </p>
              <Link to="/research/miami-used-car-market-august-2026" className="mt-3 inline-flex font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4">
                Read the Miami Used Car Market Report
              </Link>
            </section>
          )}
          {!market && (
            <section className="mt-8 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Explore local Best Deals</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Looking in South Florida? Browse vehicles priced below their Meshum Estimate within 50 miles of central Miami.
              </p>
              <Link to="/used-cars/miami-fl/best-deals" className="mt-3 inline-flex font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4">
                Best Used Car Deals in Miami, FL
              </Link>
            </section>
          )}
        </section>
      </main>
    </>
  );
}

export default InsightsBestDeals;
